const PDF_HEADER = [0x25, 0x50, 0x44, 0x46]; // %PDF
const PDF_EOF = [0x25, 0x25, 0x45, 0x4f, 0x46]; // %%EOF
const PDF_ENCRYPT = [0x2f, 0x45, 0x6e, 0x63, 0x72, 0x79, 0x70, 0x74]; // /Encrypt
const SCAN_BYTES = 1024;

export type PdfLocalCheckReason = "not-pdf" | "encrypted";

export type PdfLocalCheck =
  | { ok: true }
  | { ok: false; reason: PdfLocalCheckReason };

export const PDF_CONTENTS_ERROR =
  "This file is not a valid PDF and cannot be uploaded";
export const PDF_PASSWORD_ERROR =
  "This PDF is password-protected. Remove the password and upload again.";

export function pdfLocalCheckMessage(reason: PdfLocalCheckReason): string {
  return reason === "encrypted" ? PDF_PASSWORD_ERROR : PDF_CONTENTS_ERROR;
}

function containsBytes(
  haystack: Uint8Array,
  needle: readonly number[]
): boolean {
  outer: for (let i = 0; i <= haystack.length - needle.length; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return true;
  }
  return false;
}

function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(new Error("Could not read file"));
        return;
      }
      resolve(new Uint8Array(reader.result));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Could not read file"));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Cheap local check: %PDF + %%EOF, and reject /Encrypt (password-protected).
 * Does not detect damaged Flate streams — those still go to Ghostscript.
 */
export async function inspectPdf(file: File): Promise<PdfLocalCheck> {
  try {
    if (file.size < PDF_HEADER.length) return { ok: false, reason: "not-pdf" };

    const headLen = Math.min(file.size, SCAN_BYTES);
    const head = await readBlobBytes(file.slice(0, headLen));
    if (!containsBytes(head, PDF_HEADER))
      return { ok: false, reason: "not-pdf" };

    const tailStart = Math.max(0, file.size - SCAN_BYTES);
    const tail =
      tailStart === 0 ? head : await readBlobBytes(file.slice(tailStart));
    if (!containsBytes(tail, PDF_EOF)) return { ok: false, reason: "not-pdf" };

    if (containsBytes(head, PDF_ENCRYPT) || containsBytes(tail, PDF_ENCRYPT)) {
      return { ok: false, reason: "encrypted" };
    }

    return { ok: true };
  } catch {
    return { ok: false, reason: "not-pdf" };
  }
}

export async function looksLikePdf(file: File): Promise<boolean> {
  return (await inspectPdf(file)).ok;
}
